import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { trackFunnelStep } from '@/utils/analytics';
import { User, Calendar, Shield, ArrowLeft } from 'lucide-react';

interface OnboardingData {
  firstName: string;
  lastName: string;
  age: string;
  password: string;
  confirmPassword: string;
}

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<OnboardingData>({
    firstName: '',
    lastName: '',
    age: '',
    password: '',
    confirmPassword: ''
  });

  // Redirect if not authenticated or already completed
  useEffect(() => {
    const checkAccess = async () => {
      if (!loading) {
        if (!user || !user.email_confirmed_at) {
          toast({
            title: "Authentication required",
            description: "Please verify your email address first.",
            variant: "destructive",
          });
          navigate('/');
          return;
        }

        // Check if already completed onboarding
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed, first_name, last_name')
          .eq('user_id', user.id)
          .single();

        if (profile?.onboarding_completed) {
          navigate('/dashboard');
          return;
        }

        // If profile exists but not complete, start from appropriate step
        if (profile?.first_name && profile?.last_name) {
          setCurrentStep(2); // Skip to age if name already exists
        }
      }
    };

    checkAccess();
  }, [user, loading, navigate, toast]);

  const handleNext = () => {
    if (currentStep === 1) {
      if (!data.firstName.trim() || !data.lastName.trim()) {
        toast({
          title: "Required fields",
          description: "Please enter your first and last name.",
          variant: "destructive",
        });
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      const age = parseInt(data.age);
      if (!data.age || age < 18 || age > 100) {
        toast({
          title: "Invalid age",
          description: "Please enter a valid age between 18 and 100.",
          variant: "destructive",
        });
        return;
      }
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (currentStep !== 3) return;

    // Validate password
    if (data.password.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }

    if (data.password !== data.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are identical.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Step 1: Update password
      const { error: passwordError } = await supabase.auth.updateUser({
        password: data.password,
        data: { 
          requires_password_reset: false,
          has_password: true 
        }
      });

      if (passwordError) throw passwordError;

      // Step 2: Update profile with all data
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          user_id: user!.id,
          first_name: data.firstName,
          last_name: data.lastName,
          age: parseInt(data.age),
          onboarding_completed: true,
        });

      if (profileError) throw profileError;

      trackFunnelStep('account_created');
      
      toast({
        title: "Welcome to Safurra!",
        description: "Your account is ready. Let's find you the perfect life insurance policy.",
      });
      
      navigate('/dashboard');
      
    } catch (error: any) {
      console.error('Onboarding completion error:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !user.email_confirmed_at) {
    return null;
  }

  const stepIcons = [User, Calendar, Shield];
  const stepTitles = ["Your name", "Your age", "Secure your account"];
  const stepDescriptions = [
    "Let's get to know you",
    "We need this for accurate quotes",
    "Create a password to protect your account"
  ];

  const CurrentIcon = stepIcons[currentStep - 1];
  const progress = (currentStep / 3) * 100;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto shadow-lg border-0" style={{ boxShadow: 'var(--card-shadow)' }}>
        <CardHeader className="text-center pb-6">
          <div className="mb-4">
            <Progress value={progress} className="h-2 mb-4" />
            <div className="text-sm text-muted-foreground">
              Step {currentStep} of 3
            </div>
          </div>
          <div className="mx-auto mb-4 bg-primary/10 p-3 rounded-full w-fit">
            <CurrentIcon className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            {stepTitles[currentStep - 1]}
          </CardTitle>
          <p className="text-muted-foreground">
            {stepDescriptions[currentStep - 1]}
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  value={data.firstName}
                  onChange={(e) => setData(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="Enter your first name"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  value={data.lastName}
                  onChange={(e) => setData(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Enter your last name"
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                type="number"
                min="18"
                max="100"
                value={data.age}
                onChange={(e) => setData(prev => ({ ...prev, age: e.target.value }))}
                placeholder="Enter your age"
                autoFocus
              />
              <p className="text-sm text-muted-foreground mt-2">
                This helps us provide more accurate insurance quotes
              </p>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="password">Create password</Label>
                <Input
                  id="password"
                  type="password"
                  value={data.password}
                  onChange={(e) => setData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Create a secure password"
                  autoFocus
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Must be at least 8 characters long
                </p>
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={data.confirmPassword}
                  onChange={(e) => setData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm your password"
                />
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="flex-1"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            
            {currentStep < 3 ? (
              <Button 
                onClick={handleNext}
                className={`${currentStep === 1 ? 'w-full' : 'flex-1'}`}
              >
                Continue
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? "Setting up..." : "Complete Setup"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingFlow;